/**
 * `/liturgies/{id}/created` — save your access link.
 *
 * Reached only after creating a celebration **without an account**, and it exists because of one
 * fact: that Administrator Link is the only way back. There is no email to reset, no username to
 * recover, no support route that can restore it — we hold a fingerprint of the link and nothing
 * else. Losing it means losing the celebration.
 *
 * So this screen is not a confirmation with a link on it. It is a handover, and it refuses to let
 * the reader continue until they say they have kept the link. That friction is deliberate and is
 * the only place in the application where an anonymous path is made harder on purpose.
 *
 * The link is passed through router state rather than fetched: **there is no route that could
 * return it.** A reload therefore loses it, which is exactly the state the copy describes.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams, useRouterState } from '@tanstack/react-router';

import './liturgies.css';

interface CreatedState {
  administratorLink?: { url: string; short_url: string } | undefined;
}

export function CreatedScreen(): React.JSX.Element {
  const { id } = useParams({ from: '/liturgies/$id/created' });
  const { t } = useTranslation('common');
  const navigate = useNavigate();

  const state = useRouterState({ select: (s) => s.location.state as CreatedState | undefined });
  const link = state?.administratorLink;

  const [kept, setKept] = useState(false);

  // A reload, a back-button return, or a shared URL. The link is genuinely gone — saying so plainly
  // is better than an empty panel that looks broken.
  if (!link) {
    return (
      <div className="lit-created">
        <section className="lit-panel">
          <h1 className="lit-created__title">{t('liturgies.celebration_ready')}</h1>
          <p className="lit-created__body">
            The access link was shown when it was created and cannot be shown again — we keep only a
            fingerprint of it, never the link itself.
          </p>
          <p className="lit-created__body">
            If you still have it open somewhere, keep it. If not, whoever holds an administrator
            link for this celebration can issue you a new one from its Access page.
          </p>
          <p className="lit-created__acts">
            <Link className="lit-btn lit-btn--primary" to="/liturgies/$id" params={{ id }}>
              {t('action.continue', 'Continue')}
            </Link>
          </p>
        </section>
      </div>
    );
  }

  return (
    <div className="lit-created">
      <section className="lit-panel lit-panel--warm">
        <p className="lit-eyebrow">{t('liturgies.keep_safe')}</p>
        <h1 className="lit-created__title">{t('liturgies.link_get_back')}</h1>

        <p className="lit-created__body lit-created__body--lead">
          You created this celebration without an account, so there is no password to reset and no
          email to recover. This link is the only way back to it.
        </p>

        <LinkBlock label="Your administrator link" value={link.url} />
        <LinkBlock label="Short version, for writing down" value={link.short_url} />

        <p className="lit-created__fine">
          Email it to yourself, bookmark it, or write it down. Anyone who has it has full access to
          this celebration, so keep it as you would keep a key.
        </p>

        <label className="lit-created__check">
          <input type="checkbox" checked={kept} onChange={(e) => setKept(e.target.checked)} />
          <span>I have saved this link somewhere I will find it again.</span>
        </label>

        <p className="lit-created__acts">
          {/* Gated deliberately — the one place an anonymous path is made harder on purpose. */}
          <button
            type="button"
            className="lit-btn lit-btn--primary"
            disabled={!kept}
            onClick={() => void navigate({ to: '/liturgies/$id', params: { id } })}
          >
            Continue to planning
          </button>
        </p>

        <p className="lit-created__fine">
          You can create an account later and claim this celebration, if you would rather not depend
          on a link. Nothing you plan in the meantime is lost by doing so.
        </p>
      </section>
    </div>
  );
}

/**
 * Shown outright rather than masked.
 *
 * The opposite decision from the Access screen, for the opposite situation: there, somebody is
 * handing a link to another person from an office where a monitor faces the room. Here, the reader
 * is alone with the one thing they must not lose, and hiding it behind a control they might not
 * press is the larger risk.
 */
function LinkBlock({ label, value }: { label: string; value: string }): React.JSX.Element {
  const { t } = useTranslation('common');
  const [copied, setCopied] = useState(false);

  return (
    <div className="lit-created__link">
      <span className="lit-created__link-label">{label}</span>
      <code className="lit-created__link-value">{value}</code>
      <button
        type="button"
        className="lit-btn lit-btn--secondary"
        onClick={() => {
          void navigator.clipboard
            .writeText(value)
            .then(() => {
              setCopied(true);
              window.setTimeout(() => setCopied(false), 2500);
            })
            // Refused clipboard access is fine: the link is on screen and selectable.
            .catch(() => undefined);
        }}
      >
        {copied ? t('action.copied', 'Copied') : t('action.copy', 'Copy')}
      </button>
    </div>
  );
}
